import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { TeamMember_Plain } from "../../types/api/team-member";
import { Article_Plain } from "../../types/api/article";
import {
  getStrapiMedia,
  getStrapiResponse,
  getBlobAssetUrl,
} from "../../utils/utils";
import "./TeamMemberPage.scss";

interface TeamMemberQueryResponse {
  data: TeamMember_Plain[];
}

interface ArticlesQueryResponse {
  data: Article_Plain[];
}

const fetchTeamMember = async (
  slug: string,
  locale: string,
): Promise<TeamMemberQueryResponse> => {
  const response = await getStrapiResponse(
    `/api/team-members?filters[slug][$eq]=${slug}&customPopulate=nested${locale ? `&locale=${locale}` : ""}`,
  );
  if (!response.ok) {
    throw new Error("Failed to fetch team member");
  }
  return response.json();
};

const fetchAuthoredArticles = async (
  memberDocumentId: string,
  locale: string,
): Promise<ArticlesQueryResponse> => {
  const response = await getStrapiResponse(
    `/api/articles?filters[authorProfile][documentId][$eq]=${memberDocumentId}&customPopulate=nested&pagination[pageSize]=100${locale ? `&locale=${locale}` : ""}`,
  );
  if (!response.ok) {
    return { data: [] };
  }
  return response.json();
};

const fetchReviewedArticles = async (
  memberDocumentId: string,
  locale: string,
): Promise<ArticlesQueryResponse> => {
  const response = await getStrapiResponse(
    `/api/articles?filters[reviewerProfile][documentId][$eq]=${memberDocumentId}&customPopulate=nested&pagination[pageSize]=100${locale ? `&locale=${locale}` : ""}`,
  );
  if (!response.ok) {
    return { data: [] };
  }
  return response.json();
};

const TeamMemberPage = ({ locale }: { locale: string }) => {
  const { t } = useTranslation();
  const params = useParams<{ slug: string }>();
  const slug = params.slug || "";

  const {
    data: memberData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [`team-member-${slug}-${locale}`],
    queryFn: () => fetchTeamMember(slug, locale),
    enabled: !!slug,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const member = memberData?.data?.[0] as any;
  const memberDocumentId = member?.documentId;

  const { data: authoredData } = useQuery({
    queryKey: [`authored-articles-${memberDocumentId}-${locale}`],
    queryFn: () => fetchAuthoredArticles(memberDocumentId!, locale),
    enabled: !!memberDocumentId,
  });

  const { data: reviewedData } = useQuery({
    queryKey: [`reviewed-articles-${memberDocumentId}-${locale}`],
    queryFn: () => fetchReviewedArticles(memberDocumentId!, locale),
    enabled: !!memberDocumentId,
  });

  const authoredArticles = authoredData?.data || [];
  const reviewedArticles = reviewedData?.data || [];

  if (isLoading) {
    return <div className="loading-block"></div>;
  }

  if (isError || !member) {
    return (
      <div className="team-member-page__error">
        {t("Team member not found")}
      </div>
    );
  }

  // Map internal locale 'uk' to URL prefix 'ua'
  const urlLocale = locale === "uk" ? "ua" : locale;
  const profileLocale = urlLocale && urlLocale !== "en" ? `/${urlLocale}` : "";

  const teamBlock = member.teamBlock;
  const aboutBlock = member.aboutBlock;
  const teamMember = teamBlock?.member;

  return (
    <div className="team-member-page">
      {/* Block 1: Team (author.team) — single member */}
      {teamBlock && teamMember && (
        <div className="esg-block-team">
          <div className="wrapper">
            <div className="esg-team-list">
              <div
                className={`esg-team-list__item ${teamMember.variant === "full-photo" ? "full-photo" : ""}`}
              >
                <div className="esg-team-list__img">
                  {teamMember.photo && (
                    <img
                      src={getStrapiMedia(teamMember.photo.url)}
                      width="680"
                      height="680"
                      alt={teamMember.name || ""}
                    />
                  )}
                </div>
                <div className="esg-team-list__text">
                  {teamMember.nameLink ? (
                    <a
                      href={teamMember.nameLink}
                      className="esg-team-list__name-link"
                    >
                      <h1>{teamMember.name}</h1>
                    </a>
                  ) : (
                    <h1>{teamMember.name}</h1>
                  )}
                  {teamMember.position && (
                    <div className="esg-team-list__pos">
                      {teamMember.position}
                    </div>
                  )}
                  {teamMember.bio && (
                    <div className="esg-team-list__cont">
                      <BlocksRenderer content={teamMember.bio} />
                    </div>
                  )}
                  {teamMember.socialLink && (
                    <div className="header-social">
                      <ul>
                        <li>
                          <a
                            href={teamMember.socialLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              width="50"
                              alt="social"
                              src={
                                teamMember.socialIcon
                                  ? getStrapiMedia(teamMember.socialIcon.url)
                                  : getBlobAssetUrl("soc_1_4c09f64443.svg")
                              }
                            />
                          </a>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block 2: About Us (author.about) */}
      {aboutBlock &&
        (() => {
          const aboutUsClasses = [
            "about-us",
            "about-us-esg",
            aboutBlock.backgroundColor === "gray" && "about-us_grey-bg",
            aboutBlock.topMargin === "small" && "about-us_with-small-space",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <section className={aboutUsClasses}>
              <div className="wrapper">
                <div className="about-us__left">
                  {aboutBlock.blockTitle && (
                    <h2 className="block-title">{aboutBlock.blockTitle}</h2>
                  )}
                </div>
                <div className="about-us__right">
                  {aboutBlock.column1Text && (
                    <div className="about-us__col">
                      <BlocksRenderer content={aboutBlock.column1Text} />
                    </div>
                  )}
                  {aboutBlock.column2Text && (
                    <div className="about-us__col">
                      <BlocksRenderer content={aboutBlock.column2Text} />
                    </div>
                  )}
                  {aboutBlock.buttonText && (
                    <div className="about-us__btn">
                      <a
                        className="btn-black"
                        href={aboutBlock.buttonLink || "#"}
                      >
                        {aboutBlock.buttonText}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })()}

      {/* Authored articles */}
      {authoredArticles.length > 0 && (
        <div className="team-member-page__articles" style={{ display: "none" }}>
          <div className="wrapper">
            <h2>{t("Articles by")}</h2>
            <ul className="team-member-page__article-list">
              {authoredArticles.map((article) => (
                <li key={article.id}>
                  <a href={`${profileLocale}/news/${article.slug}`}>
                    {article.name}
                  </a>
                  {article.date && (
                    <span className="team-member-page__article-date">
                      {article.date}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Reviewed articles */}
      {reviewedArticles.length > 0 && (
        <div className="team-member-page__articles" style={{ display: "none" }}>
          <div className="wrapper">
            <h2>{t("Articles reviewed by")}</h2>
            <ul className="team-member-page__article-list">
              {reviewedArticles.map((article) => (
                <li key={article.id}>
                  <a href={`${profileLocale}/news/${article.slug}`}>
                    {article.name}
                  </a>
                  {article.date && (
                    <span className="team-member-page__article-date">
                      {article.date}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberPage;
